import type { PlayerAchievementBadge } from '@ottd/api';

import { Laurel } from '@/components/prototype/laurel';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { AchievementTone } from '@/lib/achievements';
import { achievementIcon, achievementLabel, achievementTone } from '@/lib/achievements';
import { tr } from '@/lib/locale';
import { cn } from '@/lib/utils';
import { formatDate } from '@/pages/shared';

/*
 * Achievement components. `AchievementBadge` is the profile-grid tile: wreath
 * with a level pill at its foot (none for tierless badges); title/description/
 * date live in its tooltip. `AchievementIconRow` is the compact icon strip on leaderboard
 * rows / podium cards (tooltip = label).
 * Every badge is a lucide icon framed by the laurel wreath; namespaces without
 * a curated icon get a generic award, so nothing renders empty.
 */

/** Fraction of the wreath box the inner icon occupies (fits the open centre). */
const ICON_SCALE = 0.5;

/** Fraction of the wreath box the soft glow behind the icon covers. */
const GLOW_SCALE = 0.9;

/** Wreath size (px) of the profile-grid tile. */
const TILE_LAUREL_SIZE = 64;

/** Icon size (px) inside the profile-grid tile (smaller than the strip ratio). */
const TILE_ICON_SIZE = 24;

/** Per-namespace tile icon sizes for glyphs that read visually larger. */
const TILE_ICON_SIZE_OVERRIDES: Record<string, number> = {
  games_won: 20,
};

function tileIconSize(keyName: string): number {
  return Object.hasOwn(TILE_ICON_SIZE_OVERRIDES, keyName)
    ? TILE_ICON_SIZE_OVERRIDES[keyName]
    : TILE_ICON_SIZE;
}

/** Laurel + glow classes per colour family (static strings so Tailwind emits them). */
const TONE_CLASSES: Record<AchievementTone, { laurel: string; glow: string }> = {
  gold: { laurel: 'text-gold', glow: 'from-gold/35 via-gold/10' },
  green: { laurel: 'text-green', glow: 'from-green/35 via-green/10' },
  silver: { laurel: 'text-silver', glow: 'from-silver/35 via-silver/10' },
  orange: { laurel: 'text-orange', glow: 'from-orange/35 via-orange/10' },
};

/** Lucide icon centred inside the laurel wreath, coloured by namespace. */
function AchievementIcon({
  keyName,
  size,
  iconSize = Math.round(size * ICON_SCALE),
}: {
  keyName: string;
  size: number;
  /** Inner icon size (px); defaults to half the wreath box. */
  iconSize?: number;
}) {
  const Icon = achievementIcon(keyName);
  const tone = TONE_CLASSES[achievementTone(keyName)];
  const glowSize = Math.round(size * GLOW_SCALE);
  return (
    <span
      className={cn('relative inline-block shrink-0', tone.laurel)}
      style={{ width: size, height: size }}
    >
      <Laurel className="absolute inset-0 h-full w-full" />
      <span
        className={cn(
          'bg-radial absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full via-40% to-transparent to-70%',
          tone.glow,
        )}
        style={{ width: glowSize, height: glowSize }}
        aria-hidden
      />
      <Icon
        className="text-strong absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{ width: iconSize, height: iconSize }}
        strokeWidth={2.25}
        aria-hidden
      />
    </span>
  );
}

interface AchievementBadgeProps {
  keyName: string;
  /** API-provided title; falls back to the humanized key. */
  label?: string;
  /** Catalogue description shown in the hover tooltip. */
  description?: string;
  /** Tier level; null for tierless badges (no pill). */
  level: number | null;
  /** ISO unlock timestamp shown in the hover tooltip. */
  achievedAt?: string | null;
  className?: string;
}

/** Hover card: title, description and the unlock date (legacy profile tooltip). */
function AchievementTooltipBody({
  title,
  description,
  level,
  achievedAt,
}: {
  title: string;
  description?: string;
  /** Shown as "LV n" beside the title when given. */
  level?: number | null;
  achievedAt?: string | null;
}) {
  return (
    <div className="flex max-w-56 flex-col gap-1 py-0.5">
      <span className="flex items-baseline gap-2">
        <span className="text-orange font-display text-[8px] uppercase leading-tight">{title}</span>
        {level !== null && level !== undefined && (
          <span className="text-dim font-mono text-[10px]">LV {level}</span>
        )}
      </span>
      {description && <span className="text-text text-xs leading-snug">{description}</span>}
      {achievedAt && (
        <span className="text-dim text-[10px]">
          {tr('common:achievements.earned', { date: formatDate(achievedAt) })}
        </span>
      )}
    </div>
  );
}

/** "LV n" pill anchored to the wreath's foot; nothing for tierless badges. */
function LevelPill({ level, size = 'md' }: { level: number | null; size?: 'sm' | 'md' }) {
  if (level === null) return null;
  return (
    <Badge
      variant="orange"
      className={cn(
        'absolute bottom-0 left-1/2 -translate-x-1/2',
        size === 'md' ? 'px-1.5 py-0 text-[8px]' : 'px-1 py-0 text-[6px] leading-tight',
      )}
    >
      LV {level}
    </Badge>
  );
}

export function AchievementBadge({
  keyName,
  label,
  description,
  level,
  achievedAt,
  className,
}: AchievementBadgeProps) {
  const text = label ?? achievementLabel(keyName);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn('relative inline-flex justify-center pb-2', className)}
          aria-label={text}
        >
          <AchievementIcon
            keyName={keyName}
            size={TILE_LAUREL_SIZE}
            iconSize={tileIconSize(keyName)}
          />
          <LevelPill level={level} />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <AchievementTooltipBody
          title={text}
          description={description}
          level={level}
          achievedAt={achievedAt}
        />
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Compact medal strip (standings rows / podium cards): wreath + small level
 * pill per earned badge, with the profile tooltip (title, level, description, date).
 */
export function AchievementIconRow({
  badges,
  size = 20,
  iconSize,
  className,
}: {
  badges: PlayerAchievementBadge[];
  size?: number;
  /** Inner icon size (px); defaults to half the wreath box. */
  iconSize?: number;
  className?: string;
}) {
  if (badges.length === 0) return null;
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {badges.map((badge) => (
        <Tooltip key={badge.key}>
          <TooltipTrigger asChild>
            <span className="relative inline-flex pb-1" aria-label={badge.label}>
              <AchievementIcon keyName={badge.key} size={size} iconSize={iconSize} />
              <LevelPill level={badge.level} size="sm" />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <AchievementTooltipBody
              title={badge.label}
              description={badge.description}
              level={badge.level}
              achievedAt={badge.achievedAt}
            />
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}
